import type { WorkItemOp, WorkItemSummary } from '../types/workflow.js';
import { isPracticeItem } from './workItems.js';

/**
 * The rejection vocabulary, per item kind.
 *
 * Until 2026-07-30 this was one global list — `Incorrect / Incomplete / Naming / Duplicate /
 * Other` — shared by structural work items, micro-lesson drafts and exam-level practice items.
 * That is structural-work-item language: **neither of the two real practice-item rejections in the
 * Academic Director's session-05 review fitted any option, so both landed on `Other:` with free
 * prose** and we cancelled two items with no machine-readable reason for either
 * (`AUTHORING-AGENT-BRIEF-2026-07-29.md` §4.1; AD-30).
 *
 * Shaped after `ratingConfig.ts` on purpose: a kind discriminator, a default map, and a
 * course-override hook that is deliberately empty in v1. A course can replace one kind's list
 * without touching the stored reasons or the API contract.
 *
 * The vocabulary is NOT a licence to reject more. §4.3 is the governing policy: approve anything
 * good enough to put in front of a candidate today. These options exist so that the rare genuine
 * rejection carries information.
 */
export type RejectionItemKind = 'practice_item' | 'lesson' | 'structural';

export interface RejectionReason {
  /** Reviewer-facing chip text. Also the token carried by the notes transport — see below. */
  label: string;
  /** Chip tooltip. Without it two reviewers pick different chips for the same defect. */
  hint: string;
  /** `Other` carries no information without prose, so its note is mandatory (§4.1). */
  requiresNote?: true;
}

export type RejectionOverrideMap = Readonly<
  Record<string, Partial<Record<RejectionItemKind, readonly RejectionReason[]>>>
>;

const OTHER: RejectionReason = {
  label: 'Other',
  hint: 'Anything not covered above — say what in the note.',
  requiresNote: true,
};

const DEFAULT_REASONS: Record<RejectionItemKind, readonly RejectionReason[]> = {
  // Owner-approved 2026-07-30, verbatim from §4.1 of the authoring-agent brief.
  practice_item: [
    { label: 'Wrong answer key', hint: 'The keyed option is not the correct one.' },
    { label: 'Factually incorrect', hint: 'A stem, option or explanation states something untrue.' },
    { label: 'Outside current curriculum', hint: 'Correct, but not in the current-edition reading.' },
    { label: 'Will not render', hint: 'Broken markup, missing field, visibly wrong on screen.' },
    { label: 'Duplicate', hint: 'The same item already exists.' },
    OTHER,
  ],
  // Proposed, not owner-approved: the four blocking classes from §4.3 carried over from practice
  // items, plus `Incomplete` — a lesson, unlike a single exam item, can be a genuinely unfinished
  // draft (a step with no feedback, a dead-end branch). Deliberately no engagement/style option:
  // §4.3 makes engagement explicitly non-blocking, and an option for it would invite the
  // rejections that policy is trying to stop.
  lesson: [
    { label: 'Factually incorrect', hint: 'A step, response or explanation states something untrue.' },
    { label: 'Wrong answer key', hint: 'A step keys the wrong response as correct.' },
    { label: 'Outside current curriculum', hint: 'Correct, but not in the current-edition reading.' },
    { label: 'Incomplete', hint: 'The draft stops short — missing steps, missing feedback, or a dead end.' },
    { label: 'Will not render', hint: 'Broken markup, missing field, visibly wrong on screen.' },
    { label: 'Duplicate', hint: 'The same lesson already exists.' },
    OTHER,
  ],
  // Structural items rename, move, reorder and archive nodes, so `Naming` stays meaningful here:
  // a rename item genuinely can be rejected for its proposed name.
  structural: [
    { label: 'Incorrect', hint: 'The proposed change is wrong for this node.' },
    { label: 'Incomplete', hint: 'The change is partial — it leaves the structure inconsistent.' },
    { label: 'Naming', hint: 'The proposed name is wrong, unclear or inconsistent with its siblings.' },
    { label: 'Duplicate', hint: 'Another item already makes this change.' },
    OTHER,
  ],
};

// Intentionally empty in v1, exactly as `ratingConfig.ts` COURSE_OVERRIDES is. An override replaces
// a kind's complete list, so it must itself end with an `Other`-style entry carrying
// `requiresNote: true` — nothing validates that at runtime, and dropping it would quietly return
// free-prose rejections to that course. `rejectionReasons.test.ts` asserts the invariant for the
// defaults only.
const COURSE_OVERRIDES: RejectionOverrideMap = {};

export function getRejectionReasons(courseKey: string, kind: RejectionItemKind): RejectionReason[] {
  return resolveRejectionReasons(courseKey, kind, COURSE_OVERRIDES);
}

export function resolveRejectionReasons(
  courseKey: string,
  kind: RejectionItemKind,
  overrides: RejectionOverrideMap,
): RejectionReason[] {
  return [...(overrides[courseKey]?.[kind] ?? DEFAULT_REASONS[kind])];
}

/** Ops that change where a node sits or what it is called, not what it teaches. */
const STRUCTURAL_OPS: ReadonlySet<WorkItemOp> = new Set<WorkItemOp>(['rename', 'move', 'reorder']);

/**
 * Which vocabulary an item gets, decided from the PERSISTED target/op fields only.
 *
 * Deliberately not `isLessonDraft`, which additionally requires a non-empty `steps` array in the
 * patch: `MicroLessonReview` is reachable for a draft with no steps (it renders "This draft has no
 * lesson steps." at `MicroLessonReview.tsx`), and routing on patch contents handed exactly that
 * unfinished lesson the structural `Naming`/`Incorrect` set while withholding `Incomplete` — the
 * one option it needed (gpt-5.6-sol, 2026-07-30). Target + op is stable and gives the two review
 * surfaces and the dashboard row the same answer for the same item.
 *
 * `rename`/`move`/`reorder` stay structural on an entity as well as a group, because that is where
 * `Naming` earns its place: a rename really can be rejected for the name it proposes.
 */
export function rejectionItemKind(item: WorkItemSummary): RejectionItemKind {
  if (isPracticeItem(item) || item.targetType === 'task') return 'practice_item';
  if (item.targetType === 'group' || STRUCTURAL_OPS.has(item.op)) return 'structural';
  return 'lesson';
}

export function findRejectionReason(
  reasons: readonly RejectionReason[],
  label: string,
): RejectionReason | undefined {
  return reasons.find((reason) => reason.label === label);
}

export function rejectionNoteRequired(reasons: readonly RejectionReason[], label: string): boolean {
  return findRejectionReason(reasons, label)?.requiresNote === true;
}

/**
 * The single enforcement predicate shared by all three rejection surfaces: a reason from the
 * vocabulary is mandatory, and `Other` additionally needs free text. Prose with no reason is not a
 * rejection reason — that is precisely how session 05 produced two cancelled items and no
 * machine-readable reason for either.
 */
export function canConfirmRejection(
  reasons: readonly RejectionReason[],
  label: string,
  note: string,
): boolean {
  const reason = findRejectionReason(reasons, label);
  if (!reason) return false;
  return reason.requiresNote !== true || note.trim().length > 0;
}
